import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Stack, TextField, InputAdornment, IconButton, Paper, List, ListItemButton,
  ListItemAvatar, Avatar, ListItemText, Typography, Box,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { clientApi, mediaUrl } from 'services/api'
import { useAnalytics } from 'hooks/useAnalytics'

const LiveSearch = ({ onCloseDrawer, autoFocus }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const analytics = useAnalytics()
  const inputRef = useRef()
  const debounceRef = useRef(null)
  const wrapperRef = useRef()

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus()
  }, [autoFocus])

  useEffect(() => {
    setShowSuggestions(false)
    setSuggestions([])
  }, [location.pathname])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return 0
    }
    try {
      const { data } = await clientApi.products.search(query)
      const items = (data?.data ?? []).map((p) => ({
        _id: p.id,
        title: p.name,
        thumbnail: mediaUrl(p.primary_image || p.thumbnail_url),
        slug: p.slug,
      }))
      setSuggestions(items)
      setShowSuggestions(items.length > 0)
      return items.length
    } catch {
      setSuggestions([])
      setShowSuggestions(false)
      return 0
    }
  }, [])

  const handleSearch = useCallback((query, resultsCount = 0) => {
    const trimmedQuery = query.trim()
    if (trimmedQuery.length < 2 && trimmedQuery !== '') return
    if (trimmedQuery) {
      analytics.trackSearch(trimmedQuery, resultsCount)
    }
    if (location.pathname === '/products/search') {
      navigate(trimmedQuery ? `/products/search?q=${encodeURIComponent(trimmedQuery)}` : '/products/search', { replace: true })
    } else if (trimmedQuery) {
      navigate(`/products/search?q=${encodeURIComponent(trimmedQuery)}`)
    }
    setShowSuggestions(false)
    if (onCloseDrawer) onCloseDrawer()
  }, [analytics, navigate, location.pathname, onCloseDrawer])

  const onInputChange = (e) => {
    const query = e.target.value
    setSearchTerm(query)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      const resultsCount = await fetchSuggestions(query)
      if (query.length >= 2) handleSearch(query, resultsCount)
    }, 400)
  }

  const selectSuggestion = (item) => {
    setSearchTerm(item.title)
    setShowSuggestions(false)
    navigate(`/products/${item.slug || item._id}`)
    if (onCloseDrawer) onCloseDrawer()
  }

  const clearSearch = () => {
    setSearchTerm('')
    setSuggestions([])
    if (location.pathname === '/products/search') navigate('/products/search', { replace: true })
    if (onCloseDrawer) onCloseDrawer()
  }

  return (
    <Stack ref={wrapperRef} spacing={2} sx={{ flex: '1 1 0', maxWidth: '670px', mx: 'auto', position: 'relative' }}>
      <TextField
        fullWidth
        value={searchTerm}
        onChange={onInputChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder="Search kurtas, sarees, lehengas…"
        inputRef={inputRef}
        sx={{ '& .MuiOutlinedInput-root fieldset': { borderRadius: 10 } }}
        InputProps={{
          size: 'small',
          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton onClick={clearSearch} size="small"><ClearIcon /></IconButton>
            </InputAdornment>
          ),
        }}
      />

      {showSuggestions && (
        <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300, mt: 0.5, borderRadius: 3, overflow: 'hidden' }}>
          <List dense disablePadding>
            {suggestions.map((item) => (
              <ListItemButton key={item._id} onClick={() => selectSuggestion(item)}>
                <ListItemAvatar>
                  <Avatar variant="rounded" src={item.thumbnail} alt="" sx={{ width: 40, height: 40 }} />
                </ListItemAvatar>
                <ListItemText primary={item.title} />
              </ListItemButton>
            ))}
            <ListItemButton
              component={Link}
              to={`/products/search?q=${encodeURIComponent(searchTerm)}`}
              onClick={() => {
                analytics.trackSearch(searchTerm.trim(), suggestions.length)
                setShowSuggestions(false)
              }}
            >
              <ListItemText primary={<Typography variant="body2" color="primary" fontWeight={600}>View all results for &quot;{searchTerm}&quot;</Typography>} />
            </ListItemButton>
          </List>
        </Paper>
      )}
    </Stack>
  )
}

export default LiveSearch
